import { useCorner } from '../state/useCorner';
import { useStrings } from '../state/useLocale';
import { sound } from '../lib/sound';

/**
 * The handle that folds the corner cluster into the edge of the window.
 *
 * The cluster grew one control at a time, and each arrival was argued for on
 * its own: sound carries the feedback colour normally would, somebody in the
 * wrong language cannot navigate a settings page written in it, somebody on a
 * projector needs the light theme from inside a level, and a room nobody can
 * see is a room nobody is in. Every one of those arguments is still true. What
 * none of them answered is that four permanent controls are two hundred pixels
 * of interface sitting on the last two lines of whatever is being read — a
 * chapter essay, a debrief, the foot of the level list.
 *
 * So the cluster keeps its corner and gains a door out of it. One click tucks
 * all four off the inline edge; what is left is this, twenty pixels wide,
 * against the window edge, pointing back the way they went. Nothing is
 * dismissed, nothing is buried in a menu, and the way back is visible from
 * wherever the reader is — which is the same standard every control in the
 * cluster was admitted on.
 *
 * It is the quietest thing in the corner on purpose. A control whose job is
 * to reduce the furniture cannot arrive as more of it, so it sits at
 * two-fifths strength until it is pointed at.
 *
 * The one thing it says out loud is that something is waiting. Chat is the
 * only control in the cluster that changes while nobody is looking at it, and
 * folding the corner would otherwise hide a conversation rather than quieten
 * it; the dock reports its unread count to lib/corner and the fold carries a
 * dot until the bubble is back on screen to say the number properly.
 *
 * `aria-expanded` rather than `aria-pressed`: this is a disclosure — the
 * controls it governs are removed from the page and from the tab order with
 * `visibility`, not merely moved — and it is deliberately without
 * `aria-controls`, because what it expands is five siblings in three
 * components rather than one element with an id.
 *
 * No `dir` attribute, for the reason ThemeToggle.tsx gives at length:
 * `inset-inline-end` resolves against the element's own direction, so a `dir`
 * on a fixed control moves the control. This one never renders a word, only a
 * chevron, and which way the chevron points is settled in the stylesheet
 * (styles/locale.css §4) where the rest of the mirroring lives.
 */
export function CornerFold() {
  const { folded, signal, toggle } = useCorner();
  const s = useStrings().app.corner;
  const waiting = folded && signal;

  return (
    <button
      className={waiting ? 'fold has-signal' : 'fold'}
      type="button"
      aria-expanded={!folded}
      title={folded ? (waiting ? s.titleFoldedNew : s.titleFolded) : s.titleShown}
      aria-label={folded ? (waiting ? s.labelFoldedNew : s.labelFolded) : s.labelShown}
      onClick={() => {
        sound.click();
        toggle();
      }}
    >
      {/* A single chevron, pointing the way the cluster is about to travel:
          at the edge while the controls are out, and back at them once they
          have gone. It is drawn pointing at the inline end and turned over by
          the stylesheet, so one path serves four cases — two states times two
          directions. */}
      <svg viewBox="0 0 16 16" aria-hidden="true" width="12" height="12">
        <path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {/* Never a hue, like the unread count it stands in for: the corner's one
          colour is the ring on the chat bubble, and this is not it. */}
      {waiting && <span className="fold-dot" aria-hidden="true" />}
    </button>
  );
}
